'use client'

import * as React from 'react'
import Link from 'next/link'

import { useClerk } from '@clerk/nextjs'
import { create } from 'zustand'
import { Logo } from './mkt/logo'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { redirect, usePathname } from 'next/navigation'
import { getLoginHref } from '~/app/(dash)/(login)/login/get-login-href'
import { loginRoute } from '~/constants/routing'
import { encodeNextParam } from '~/app/(mkt)/_index-redirector/server'

const debug = false

export const useLoaderStore = create<{
  isSigningOut: boolean
  setIsSigningOut: (isSigningOut: boolean) => void
}>((set) => {
  return {
    isSigningOut: false,
    setIsSigningOut: (isSigningOut: boolean) => {
      set({ isSigningOut })
    },
  }
})

export const Loader = ({
  done: _done,
  notFound,
  redirectToLoginOnNotFound,
  debugData,
  customMessage,
}: {
  done: boolean
  notFound: boolean
  redirectToLoginOnNotFound?: boolean
  debugData?: unknown
  customMessage?: React.ReactNode
}) => {
  const done = _done && !notFound
  const isSigningOut = useLoaderStore((s) => s.isSigningOut)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const logoRef = React.useRef<SVGSVGElement>(null)
  const notFoundRef = React.useRef<HTMLDivElement>(null)
  const { user, loaded: isLoaded } = useClerk()
  const [reloadedUser, setReloadedUser] = React.useState<typeof user>()
  const pathname = usePathname()

  const [minNotFoundWaitTime, setMinNotFoundWaitTime] = React.useState(false)

  const hasReloadedUserRef = React.useRef(false)

  React.useEffect(() => {
    if (!user) return
    if (hasReloadedUserRef.current) return
    user.reload().then(setReloadedUser)
    hasReloadedUserRef.current = true
  }, [user])

  const status = React.useMemo(() => {
    if (!isLoaded) return undefined
    if (!reloadedUser) return 'unauthenticated'
    if (
      !reloadedUser.username ||
      !reloadedUser.publicMetadata.hasPersonalTeam
    ) {
      return 'incomplete'
    }
    return 'authenticated'
  }, [isLoaded, reloadedUser])

  React.useEffect(() => {
    const t = setTimeout(() => {
      setMinNotFoundWaitTime(true)
    }, 1000)
    return () => {
      clearTimeout(t)
    }
  }, [])

  React.useEffect(() => {
    if (done) {
      const el = containerRef.current
      if (!el) return

      el.classList.add('invisible', 'opacity-0')

      return () => {
        el.classList.remove('invisible', 'opacity-0')
      }
    }
  }, [done])

  React.useEffect(() => {
    if (!minNotFoundWaitTime || isSigningOut) return
    if ((notFound || status !== 'authenticated') && notFoundRef.current) {
      if (
        status === 'unauthenticated' &&
        pathname.split('/').filter(Boolean).length > 1 &&
        redirectToLoginOnNotFound
      ) {
        redirect(
          getLoginHref(loginRoute + '?next=' + encodeNextParam(pathname))
        )
      }

      const logoEl = logoRef.current
      const notFoundEl = notFoundRef.current
      if (!logoEl || !notFoundEl) return

      logoEl.classList.add('grayscale')
      notFoundEl.classList.remove('opacity-0', 'invisible')

      return () => {
        logoEl.classList.remove('grayscale')
        notFoundEl.classList.add('opacity-0', 'invisible')
      }
    }
  }, [
    minNotFoundWaitTime,
    notFound,
    status,
    isSigningOut,
    redirectToLoginOnNotFound,
    pathname,
  ])

  const hasRendered = useHasRendered()

  return (
    <div
      className="fixed inset-0 z-loader flex flex-col items-center overflow-auto bg-dark-bg transition-[visibility,opacity] duration-300"
      ref={containerRef}
    >
      <main className="relative flex w-full flex-shrink-0 flex-grow items-center justify-center py-16 px-5">
        <Logo
          className="border-none [background-image:none] h-[64px] w-[64px] transition-[filter] duration-300"
          variant="mark"
          ref={logoRef}
        />
        {hasRendered &&
          !isSigningOut &&
          (notFound || status !== 'authenticated') && (
            <div
              ref={notFoundRef}
              className="invisible absolute transition-[visibility,opacity] duration-300 top-[calc(50%+80px)] flex w-full max-w-sm -translate-y-1/2 flex-col items-center text-dark-muted opacity-0"
            >
              <div className="w-full text-center text-sm">
                {!customMessage && status === 'unauthenticated' && (
                  <>
                    Not Found.
                    <br />
                    Go back{' '}
                    <Link
                      className="text-dark-control-base hover:underline"
                      href={'/'}
                    >
                      home
                    </Link>
                    .
                    {debug && (
                      <>
                        {JSON.stringify(
                          {
                            clerk: { user, reloadedUser, isLoaded },
                            debugData,
                          },
                          null,
                          2
                        )}
                      </>
                    )}
                  </>
                )}
                {!customMessage && status === 'incomplete' && (
                  <>
                    User Incomplete.
                    <br />
                    <Link
                      className="text-dark-control-base hover:underline"
                      href={'/complete-user'}
                    >
                      Complete your user
                    </Link>
                    .
                  </>
                )}
                {!customMessage && status === 'authenticated' && (
                  <>
                    Not Found
                    <br />
                    Go back to your{' '}
                    <Link
                      className="text-dark-control-base hover:underline"
                      href={`/${user?.username ?? 'dashboard'}`}
                    >
                      dashboard
                    </Link>
                    .
                  </>
                )}
                {customMessage && customMessage}
              </div>
            </div>
          )}
      </main>
    </div>
  )
}
